import { FC, Fragment, useContext, useState } from 'react';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/dynamic/i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleArrowDown,
  faCircleArrowUp,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { formatPriceDynamic } from 'utils/dynamic/dynamic';
import DynamicHotelRooms from './DynamicHotelRooms';
import { getCurrency, openInNewWindow } from 'utils/utils';
import { DynamicContext } from 'contexts/DynamicContext';
import { Hotel, RoomClass } from 'ts/hotel/interfaces/HotelResponse';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import DynamicHotelDetailsSkeleton from '../DynamicHotelDetailsSkeleton';
import { b2b } from 'constants/generic';

interface DynamicHotelProps {
  hotel?: Hotel;
  isChoosen?: boolean;
  choosenRoomPrice?: number;
  isLoading?: boolean;
  onClickChange: (room: RoomClass) => void;
  isChangeRoom?: boolean;
  numberOfPax: number;
  selectedRoom?: RoomClass;
  searchToken?: string;
}

const DynamicHotel: FC<DynamicHotelProps> = ({ hotel, ...props }) => {
  const { t, i18n } = useTranslation();
  const { currency } = useContext(DynamicContext);
  const [showRooms, setShowRooms] = useState(false);
  const roomIndex =
    hotel?.roomClasses.findIndex((room) => room.rcUniqueKey === props.selectedRoom?.rcUniqueKey) ||
    0;
  const selectedRoomIndex = !props.isChangeRoom && roomIndex !== -1 ? roomIndex : 0;

  const onClickInfo = () => {
    const params = new URLSearchParams({
      id: hotel!.htuKey,
      roomBToken: props.selectedRoom
        ? props.selectedRoom.hotelRooms[0].bToken
        : hotel!.roomClasses[0].hotelRooms[0].bToken,
      preview: 'true',
    });

    if (props.searchToken) {
      params.append('session', props.searchToken);
    }
    sessionStorage.setItem('hotelFromSearch', JSON.stringify(hotel));
    openInNewWindow('/hotel/details?' + params.toString());
  };

  const limitDate = hotel?.roomClasses[selectedRoomIndex]?.cxl.limitDate || '';

  return (
    <div className={'' + (showRooms ? ' open' : '') + (props.isChoosen ? ' ticket-choosen' : '')}>
      {hotel ? (
        <Fragment>
          <div className={'dyn-hotel'}>
            <div className="dyn-hotel-img">
              <img src={hotel.info?.picture} alt="" />
            </div>
            <div className="dyn-hotel-info">
              <div className="text-bold text-md">
                <span>{hotel.item.name}</span>
                <Rating className="dyn-hotel-star" value={hotel.item.star || hotel.rating} />
              </div>
              {!props.isChangeRoom && (
                <Fragment>
                  <div>
                    <span>{t('flight.dates')}: </span>
                    <span className="text-bold text-uppercase">
                      {dateFormat(hotel.roomClasses[0].checkIn, 'dd MMM')} -{' '}
                      {dateFormat(hotel.roomClasses[0].checkOut, 'dd MMM')}
                    </span>
                  </div>
                  <div>
                    <span>{t('mo.nights')}: </span>
                    <span className="text-bold">{hotel.roomClasses[0].nights}</span>
                  </div>
                </Fragment>
              )}
              <div>
                <span>{t('hotel.room')}: </span>
                <span className="text-bold">
                  {hotel.roomClasses[selectedRoomIndex].hotelRooms?.[0].displayName}
                </span>
              </div>
              <div>
                <span>{t('filter.board')}: </span>
                <span className="text-bold">
                  {hotel.roomClasses[selectedRoomIndex].board.basis.desc}
                </span>
              </div>
              {b2b && (
                <div>
                  <span>{t('filter.free_x')}: </span>
                  {new Date(limitDate) > new Date() ? (
                    <span className="text-bold text-success text-uppercase" dir="ltr">
                      {dateFormat(limitDate, 'dd MMM-yy', i18n.language)}
                    </span>
                  ) : (
                    <span className="text-bold color-danger ">{t('hotel.non_refundable')}</span>
                  )}
                </div>
              )}

              {!b2b && !props.isChangeRoom && (
                <div>
                  {new Date(limitDate) > new Date(2000, 0, 1) &&
                    new Date(limitDate) < new Date() && (
                      <div className="color-danger text-bold">
                        {t('hotel.full_cancellation_fee')}
                      </div>
                    )}
                  {new Date(limitDate) > new Date(2000, 0, 1) &&
                    new Date(limitDate) > new Date() && (
                      <div className="text-success text-bold">
                        {t('hotel.free_cancel_before')}{' '}
                        {dateFormat(limitDate, 'dd MMM yyyy', i18n.language)}
                      </div>
                    )}
                </div>
              )}
              {!props.isChangeRoom && !!props.searchToken && (
                <div className="dyn-hotel-actions">
                  <span className="dyn-hotel-action" onClick={() => onClickInfo()}>
                    <FontAwesomeIcon icon={faInfoCircle} />
                    {t('hotel.info')}
                  </span>
                </div>
              )}
              {props.isChangeRoom && (
                <div className="show-rooms" onClick={() => setShowRooms(!showRooms)}>
                  <FontAwesomeIcon
                    className="color-contrast mr-8"
                    icon={showRooms ? faCircleArrowUp : faCircleArrowDown}
                  />
                  {t('hotel.show_rooms')} {hotel.roomClasses?.filter((room) => room.visible).length}
                </div>
              )}
            </div>
            <div className="flex-center flex-one display-column">
              {!!props.choosenRoomPrice && (
                <Fragment>
                  <div className="text-light text-center mb-5">{t('dyn.extra_money')}</div>
                  <div className="text-bold text-md mb-10">
                    {formatPriceDynamic(
                      hotel.roomClasses[0].price[currency as keyof Price] / props.numberOfPax -
                        props.choosenRoomPrice,
                      getCurrency(currency)
                    )}
                  </div>
                </Fragment>
              )}
              <Button
                title={
                  props.isChoosen
                    ? t('dyn.your_choice')
                    : !props.isChangeRoom
                    ? t('change_choice')
                    : t('choose')
                }
                onClick={() => props.onClickChange(hotel.roomClasses[roomIndex])}
                disabled={props.isLoading}
              />
            </div>
          </div>
          <DynamicHotelRooms
            rooms={hotel.roomClasses}
            showRooms={showRooms}
            goToDetails={() => {}}
            selectedRoomPrice={props.choosenRoomPrice || 0}
            numberOfPax={props.numberOfPax}
            selectedRoomId={props.isChoosen ? props.selectedRoom?.rcUniqueKey : undefined}
            onClickChange={(roomId) => props.onClickChange(roomId)}
            isLoading={props.isLoading}
          />
        </Fragment>
      ) : (
        <DynamicHotelDetailsSkeleton />
      )}
    </div>
  );
};

export default withErrorBoundary(DynamicHotel, 'DynamicHotel');
